/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip,
    Typography,
    Stack,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    TextField,
    Autocomplete,
    CircularProgress,
    Collapse,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { dispatch, useSelector } from 'src/redux/store';
import { applyTemplate, getTemplate } from 'src/redux/slices/taskTemplateSlice';
import { cloneDefaultTemplate } from 'src/redux/slices/tasksV2ChecklistConfig';
import { updateError } from 'src/redux/slices/user';
import { CATEGORY_LABELS } from 'src/constants/TaskConstants';
import { callApi } from 'src/utils/apiCall';

// -----------------------------------------------

const SCOPE_COLORS = {
    system: { bg: '#F4ECFF', color: '#7B2FE0', label: 'System' },
    group: { bg: '#E6F4FF', color: '#0C53B7', label: 'Group' },
    practice: { bg: '#E8F5E9', color: '#1B5E20', label: 'Practice' },
};

const PRIORITY_CHIP_COLORS = {
    urgent: { bg: '#FFF1F0', color: '#FF4842' },
    high: { bg: '#FFF7CD', color: '#B76E00' },
    normal: { bg: '#E6F4FF', color: '#0C53B7' },
    low: { bg: '#F4F6F8', color: '#637381' },
};

// -----------------------------------------------

function ChecklistItemRow({ item }) {
    const hasDueOffset = (item.due_offset_days > 0) || (item.due_offset_hours > 0);
    let dueLabel = '';
    if (hasDueOffset) {
        const parts = [];
        if (item.due_offset_days > 0) parts.push(`${item.due_offset_days}d`);
        if (item.due_offset_hours > 0) parts.push(`${item.due_offset_hours}h`);
        dueLabel = `Due in ${parts.join(' ')}`;
    }

    return (
        <ListItem disableGutters sx={{ py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 28 }}>
                <Iconify
                    icon={item.is_required ? 'eva:checkmark-circle-2-outline' : 'eva:radio-button-off-outline'}
                    width={18}
                    sx={{ color: item.is_required ? 'primary.main' : 'text.disabled' }}
                />
            </ListItemIcon>
            <ListItemText
                primary={
                    <Stack direction="row" alignItems="center" spacing={0.75} component="span">
                        <span>{item.label || item.title || item.text || ''}</span>
                        {hasDueOffset && (
                            <Chip
                                label={dueLabel}
                                size="small"
                                sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    color: 'primary.main',
                                    bgcolor: (theme) => `${theme.palette.primary.main}14`,
                                    '& .MuiChip-label': { px: 0.75 },
                                }}
                            />
                        )}
                    </Stack>
                }
                secondary={item.is_required ? 'Required' : 'Optional'}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500, component: 'div' }}
                secondaryTypographyProps={{ variant: 'caption', color: item.is_required ? 'primary.main' : 'text.disabled' }}
            />
        </ListItem>
    );
}

ChecklistItemRow.propTypes = {
    item: PropTypes.shape({
        label: PropTypes.string,
        title: PropTypes.string,
        text: PropTypes.string,
        is_required: PropTypes.bool,
        due_offset_days: PropTypes.number,
        due_offset_hours: PropTypes.number,
    }).isRequired,
};

// -----------------------------------------------

function WorkflowStepRow({ step, index }) {
    const [expanded, setExpanded] = useState(false);
    const hasSubItems = step.checklist_items?.length > 0;

    return (
        <Box sx={{ mb: 1 }}>
            <ListItem
                disableGutters
                sx={{
                    py: 0.75,
                    px: 1.5,
                    bgcolor: 'background.neutral',
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <ListItemIcon sx={{ minWidth: 32 }}>
                    <Box
                        sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                        }}
                    >
                        {index + 1}
                    </Box>
                </ListItemIcon>
                <ListItemText
                    primary={step.title || step.name || `Step ${index + 1}`}
                    secondary={step.role_label || step.role || (step.due_offset_days != null ? `Due: +${step.due_offset_days}d` : null)}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                    secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                />
                {hasSubItems && (
                    <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                        <Iconify
                            icon={expanded ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'}
                            width={16}
                        />
                    </IconButton>
                )}
            </ListItem>

            {hasSubItems && (
                <Collapse in={expanded}>
                    <List dense disablePadding sx={{ pl: 4, pt: 0.5 }}>
                        {step.checklist_items.map((item, idx) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <ChecklistItemRow key={idx} item={item} />
                        ))}
                    </List>
                </Collapse>
            )}
        </Box>
    );
}

WorkflowStepRow.propTypes = {
    step: PropTypes.shape({
        title: PropTypes.string,
        name: PropTypes.string,
        role_label: PropTypes.string,
        role: PropTypes.string,
        due_offset_days: PropTypes.number,
        checklist_items: PropTypes.array,
    }).isRequired,
    index: PropTypes.number.isRequired,
};

// -----------------------------------------------

// Channel methods used by the in-migration patch table preview. Order
// chosen so PHONE / WHATSAPP / EMAIL / SMS sit first (the four common
// V1 reminder methods), with PAYMENT / OTHER / CALLBACK_REQUEST after.
const MIGRATION_METHOD_OPTIONS = [
    { value: 'PHONE',            label: 'Phone' },
    { value: 'WHATSAPP',         label: 'WhatsApp' },
    { value: 'EMAIL',            label: 'Email' },
    { value: 'SMS',              label: 'SMS' },
    { value: 'PAYMENT',          label: 'Payment' },
    { value: 'OTHER',            label: 'Other' },
    { value: 'CALLBACK_REQUEST', label: 'Callback' },
];

const V1_METHOD_CHANNEL_ROLE = 'v1_method_channel';


export default function TemplateDetail({
    open, template, practiceId, onClose, onApplied,
    onClone = null, defaultLeadId = null,
    migrationContext = false, onTestOnSample = null,
}) {
    const [applying, setApplying] = useState(false);
    const [leadSearch, setLeadSearch] = useState('');
    const [leadOptions, setLeadOptions] = useState([]);
    const [leadLoading, setLeadLoading] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);
    // Auto-show lead selector and pre-select the lead when defaultLeadId is provided
    const [showLeadSelector, setShowLeadSelector] = useState(Boolean(defaultLeadId));
    // Migration-mode: which V1 method to preview. The channel slot (item
    // with metadata.role === 'v1_method_channel') is rewritten using the
    // BE-owned channel_patch_table for that method.
    const [migrationMethod, setMigrationMethod] = useState('PHONE');
    const fullTemplate = template;

    // Migration-mode helpers — derived once per template change.
    const isMigrationTemplate = !!fullTemplate?.metadata?.is_v1_migration_template;
    const v1Group = fullTemplate?.metadata?.v1_group || null;
    // CALLBACK_REQUEST templates have no channel slot — disable the
    // selector and tell the operator why.
    const isCallbackRequestGroup = v1Group === 'CALLBACK_REQUEST';

    // Channel patch table comes from the system-templates BE response,
    // mirrored on `tasksV2DefaultTemplates.byPractice[practiceId]`. Pull
    // it out so we can apply the in-flight patch to the channel slot.
    const channelPatchTable = useSelector(
        (s) => (
            migrationContext
                ? (s.tasksV2DefaultTemplates?.byPractice?.[practiceId]
                   ?.channel_patch_table || null)
                : null
        ),
    );

    // Fetch full template details (may have more data than card)
    useEffect(() => {
        if (open && template?.id && practiceId) {
            dispatch(getTemplate(practiceId, template.id));
        }
    }, [open, template?.id, practiceId]);

    // When a defaultLeadId is provided, auto-show the lead selector and pre-fetch the lead
    useEffect(() => {
        if (open && defaultLeadId && practiceId) {
            setShowLeadSelector(true);
            callApi(`/api/v1/practices/${practiceId}/leads/${defaultLeadId}/`)
                .then((res) => {
                    if (res.status === 200 && res.data) {
                        setSelectedLead(res.data);
                    }
                });
        }
    }, [open, defaultLeadId, practiceId]);

    // Preload recent leads as soon as the lead selector opens (so user sees options without typing)
    useEffect(() => {
        if (!showLeadSelector || !practiceId || leadSearch) return;
        let active = true;
        setLeadLoading(true);
        callApi(`/api/v1/practices/${practiceId}/leads/?limit=20&ordering=-created_at`)
            .then((res) => {
                if (active && res.status === 200) {
                    const results = res.data?.results || res.data || [];
                    setLeadOptions(results);
                }
            })
            .finally(() => {
                if (active) setLeadLoading(false);
            });
        // eslint-disable-next-line consistent-return
        return () => { active = false; };
    }, [showLeadSelector, practiceId, leadSearch]);

    // Lead search debounce — fires on any typed character
    useEffect(() => {
        if (!leadSearch) return;
        let active = true;
        setLeadLoading(true);
        const handle = setTimeout(() => {
            callApi(`/api/v1/practices/${practiceId}/leads/?search=${encodeURIComponent(leadSearch)}&limit=20`)
                .then((res) => {
                    if (active && res.status === 200) {
                        const results = res.data?.results || res.data || [];
                        setLeadOptions(results);
                    }
                })
                .finally(() => {
                    if (active) setLeadLoading(false);
                });
        }, 250);
        // eslint-disable-next-line consistent-return
        return () => { active = false; clearTimeout(handle); };
    }, [leadSearch, practiceId]);

    const handleApplyStandalone = () => {
        setApplying(true);
        dispatch(
            applyTemplate(
                practiceId,
                { template_id: fullTemplate.id },
                (result) => {
                    setApplying(false);
                    if (result.success) {
                        dispatch(updateError({ success: true, message: 'Template applied \u2014 tasks created' }));
                        onApplied();
                    }
                }
            )
        );
    };

    const handleApplyToLead = () => {
        if (!selectedLead) return;
        setApplying(true);
        dispatch(
            applyTemplate(
                practiceId,
                { template_id: fullTemplate.id, lead_id: selectedLead.id },
                (result) => {
                    setApplying(false);
                    if (result.success) {
                        dispatch(updateError({ success: true, message: 'Template applied \u2014 tasks created' }));
                        onApplied();
                    }
                }
            )
        );
    };

    // Migration-mode Clone: when the source carries
    // metadata.is_v1_migration_template=true, route through the
    // migration-aware thunk (which writes the override row + preserves
    // the migration flags so the resolver still considers the clone).
    // Non-migration sources fall back to the legacy onClone prop.
    const handleMigrationClone = () => {
        if (!fullTemplate?.id) return;
        dispatch(cloneDefaultTemplate(practiceId, {
            source_template_id: fullTemplate.id,
        }));
        // Close the dialog after dispatching \u2014 the slice refreshes the
        // list itself, so the cockpit will update.
        if (onClose) onClose();
    };

    // Build the patched checklist once per (template, method) so the
    // body re-renders only when the operator switches channels.
    const checklistItemsRaw = fullTemplate?.checklist_items || [];
    const patchedChecklistItems = useMemo(() => {
        if (!migrationContext || !isMigrationTemplate) return checklistItemsRaw;
        if (isCallbackRequestGroup) return checklistItemsRaw;
        const patch = channelPatchTable && channelPatchTable[migrationMethod];
        if (!patch) return checklistItemsRaw;
        return checklistItemsRaw.map((item) => {
            const role = (item.metadata || {}).role;
            if (role !== V1_METHOD_CHANNEL_ROLE) return item;
            return {
                ...item,
                title: patch.title || item.title,
                label: patch.title || item.label,
                item_type: patch.item_type || item.item_type,
                __patched: true,
            };
        });
    }, [
        checklistItemsRaw,
        migrationContext,
        isMigrationTemplate,
        isCallbackRequestGroup,
        migrationMethod,
        channelPatchTable,
    ]);

    const scopeStyle = SCOPE_COLORS[fullTemplate.scope] || SCOPE_COLORS.practice;
    const priorityStyle = PRIORITY_CHIP_COLORS[fullTemplate.priority] || PRIORITY_CHIP_COLORS.normal;
    const isChecklist = fullTemplate.template_type === 'checklist';
    const checklistItems = patchedChecklistItems;
    const workflowSteps = fullTemplate.steps || fullTemplate.workflow_steps || [];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <Chip
                                label={scopeStyle.label}
                                size="small"
                                sx={{
                                    bgcolor: scopeStyle.bg,
                                    color: scopeStyle.color,
                                    fontWeight: 700,
                                    fontSize: '0.65rem',
                                    height: 20,
                                }}
                            />
                            <Chip
                                label={(fullTemplate.template_type || 'checklist').toUpperCase()}
                                size="small"
                                sx={{
                                    bgcolor: 'action.hover',
                                    color: 'text.secondary',
                                    fontWeight: 600,
                                    fontSize: '0.65rem',
                                    height: 20,
                                }}
                            />
                            {fullTemplate.priority && (
                                <Chip
                                    label={fullTemplate.priority}
                                    size="small"
                                    sx={{
                                        bgcolor: priorityStyle.bg,
                                        color: priorityStyle.color,
                                        fontWeight: 600,
                                        fontSize: '0.65rem',
                                        height: 20,
                                        textTransform: 'capitalize',
                                    }}
                                />
                            )}
                            {migrationContext && isMigrationTemplate && v1Group && (
                                <Chip
                                    label={`V1 group: ${v1Group}`}
                                    size="small"
                                    data-testid="template-detail-v1-group-chip"
                                    sx={{
                                        bgcolor: '#FFF4E5',
                                        color: '#7A4F01',
                                        fontWeight: 700,
                                        fontSize: '0.65rem',
                                        height: 20,
                                    }}
                                />
                            )}
                        </Stack>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                            {fullTemplate.name}
                        </Typography>
                        {fullTemplate.category && (
                            <Typography variant="caption" color="text.secondary">
                                {CATEGORY_LABELS[fullTemplate.category] || fullTemplate.category}
                            </Typography>
                        )}
                    </Box>
                    <IconButton size="small" onClick={onClose}>
                        <Iconify icon="eva:close-fill" width={20} />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 2 }}>
                {/* Description */}
                {fullTemplate.description && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.5 }}>
                            Description
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            {fullTemplate.description}
                        </Typography>
                    </Box>
                )}

                {fullTemplate.description && (isChecklist ? checklistItems.length > 0 : workflowSteps.length > 0) && (
                    <Divider sx={{ my: 2 }} />
                )}

                {migrationContext && isMigrationTemplate && (
                    <Box sx={{ mb: 2 }} data-testid="template-detail-method-selector">
                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 1 }}>
                            Preview channel slot for V1 method
                        </Typography>
                        <Tooltip
                            title={isCallbackRequestGroup
                                ? 'no channel slot — only the parent task is patched'
                                : ''}
                            disableHoverListener={!isCallbackRequestGroup}
                        >
                            <span>
                                <ToggleButtonGroup
                                    size="small"
                                    exclusive
                                    color="primary"
                                    value={isCallbackRequestGroup ? 'CALLBACK_REQUEST' : migrationMethod}
                                    onChange={(_e, val) => {
                                        if (val) setMigrationMethod(val);
                                    }}
                                    disabled={isCallbackRequestGroup}
                                    aria-label="V1 method preview"
                                >
                                    {MIGRATION_METHOD_OPTIONS.map((opt) => (
                                        <ToggleButton
                                            key={opt.value}
                                            value={opt.value}
                                            data-testid={`migration-method-${opt.value}`}
                                            sx={{ textTransform: 'none', fontSize: '0.7rem' }}
                                        >
                                            {opt.label}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </span>
                        </Tooltip>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 1 }}
                            data-testid="template-detail-method-selector-helper"
                        >
                            This is a read-only preview of how V1 reminders are migrated. To customize, clone this template for your practice using the button below.
                        </Typography>
                    </Box>
                )}

                {/* Checklist Items */}
                {isChecklist && checklistItems.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 1 }}>
                            Checklist Items ({checklistItems.length})
                        </Typography>
                        <List dense disablePadding>
                            {checklistItems.map((item, idx) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <ChecklistItemRow key={idx} item={item} />
                            ))}
                        </List>
                    </Box>
                )}

                {/* Workflow Steps */}
                {!isChecklist && workflowSteps.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 1 }}>
                            Workflow Steps ({workflowSteps.length})
                        </Typography>
                        <List dense disablePadding>
                            {workflowSteps.map((step, idx) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <WorkflowStepRow key={idx} step={step} index={idx} />
                            ))}
                        </List>
                    </Box>
                )}

                {/* Empty state */}
                {isChecklist && checklistItems.length === 0 && !workflowSteps.length && (
                    <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
                        No items defined for this template.
                    </Typography>
                )}

                {/* Lead selector */}
                {showLeadSelector && (
                    <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 1 }}>
                            Select Lead / Patient
                        </Typography>
                        <Autocomplete
                            size="small"
                            options={leadOptions}
                            loading={leadLoading}
                            value={selectedLead}
                            onChange={(_, val) => setSelectedLead(val)}
                            onInputChange={(_, val) => setLeadSearch(val)}
                            getOptionLabel={(opt) =>
                                `${opt.first_name || ''} ${opt.last_name || ''}`.trim() || opt.email || String(opt.id)
                            }
                            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Search for a lead..."
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {leadLoading ? <CircularProgress size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Box>
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                {/* Migration mode: route Clone through the migration-aware
                    thunk for migration-flagged sources; non-migration
                    sources fall back to the legacy onClone callback. */}
                {migrationContext && isMigrationTemplate ? (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleMigrationClone}
                        startIcon={<Iconify icon="eva:copy-fill" width={18} />}
                        sx={{ textTransform: 'none', mr: 'auto' }}
                        data-testid="template-detail-clone-migration"
                    >
                        Clone for this practice
                    </Button>
                ) : (
                    onClone && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => onClone(fullTemplate)}
                            startIcon={<Iconify icon="eva:copy-fill" width={18} />}
                            sx={{ textTransform: 'none', mr: 'auto' }}
                        >
                            Clone
                        </Button>
                    )
                )}
                {migrationContext && (
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => onTestOnSample && onTestOnSample(fullTemplate)}
                        disabled={!isMigrationTemplate || !onTestOnSample}
                        startIcon={<Iconify icon="mdi:test-tube" width={18} />}
                        sx={{ textTransform: 'none' }}
                        data-testid="template-detail-test-on-sample"
                    >
                        Test on sample
                    </Button>
                )}
                <Button
                    variant="outlined"
                    color="inherit"
                    onClick={onClose}
                    sx={{ textTransform: 'none' }}
                >
                    Cancel
                </Button>

                {/* In migration mode the Apply (Standalone) and Apply
                    To Lead buttons are hidden — those are post-V2 actions.
                    The lead selector branch is also unreachable because
                    nothing toggles `showLeadSelector` in migration mode. */}
                {migrationContext ? null : showLeadSelector ? (
                    <>
                        <Button
                            variant="outlined"
                            onClick={() => setShowLeadSelector(false)}
                            sx={{ textTransform: 'none' }}
                        >
                            Back
                        </Button>
                        <Button
                            variant="contained"
                            disabled={!selectedLead || applying}
                            onClick={handleApplyToLead}
                            sx={{ textTransform: 'none' }}
                            startIcon={applying ? <CircularProgress size={14} color="inherit" /> : null}
                        >
                            Apply to Lead
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            variant="outlined"
                            onClick={handleApplyStandalone}
                            disabled={applying}
                            sx={{ textTransform: 'none' }}
                            startIcon={applying ? <CircularProgress size={14} color="inherit" /> : null}
                        >
                            Apply (Standalone)
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => setShowLeadSelector(true)}
                            disabled={applying}
                            sx={{ textTransform: 'none' }}
                        >
                            Apply to Lead
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

TemplateDetail.propTypes = {
    open: PropTypes.bool.isRequired,
    template: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string,
        description: PropTypes.string,
        scope: PropTypes.string,
        category: PropTypes.string,
        template_type: PropTypes.string,
        priority: PropTypes.string,
        checklist_items: PropTypes.array,
        workflow_steps: PropTypes.array,
        // eslint-disable-next-line react/forbid-prop-types
        metadata: PropTypes.object,
    }).isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onClose: PropTypes.func.isRequired,
    onApplied: PropTypes.func.isRequired,
    /** When provided, shows a Clone button in the footer (non-migration mode). */
    onClone: PropTypes.func,
    /** When set, the lead selector is shown and this lead is pre-selected. */
    defaultLeadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Cockpit/migration mode: surfaces the V1 group chip + method
     *  selector, routes Clone through the migration-aware thunk, and
     *  hides the Apply buttons. */
    migrationContext: PropTypes.bool,
    /** Required when `migrationContext`: invoked with the active template
     *  when the operator clicks "Test on sample". The cockpit-side
     *  wrapper owns the dialog state. */
    onTestOnSample: PropTypes.func,
};

